import { useState, useEffect } from 'react';
import { getOrdersSummary } from '@/services/reportsService';
import { toastMessage } from '@/utils/toast-message';
import { TrendingUp, Package, DollarSign, Users, AlertCircle, BarChart3 } from 'lucide-react';

interface SummaryData {
  summary: {
    total_orders: number;
    total_revenue: number;
    paid_revenue: number;
    avg_order_value: number;
    unique_customers: number;
    paid_orders: number;
    refunded_orders: number;
    cancelled_orders: number;
  };
  top_tags: Array<{ tag: string; order_count: number; revenue: number }>;
  top_agents: Array<{ agent: string; order_count: number; revenue: number }>;
}

const OrdersSummaryReport = () => {
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  });
  const [toDate, setToDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<SummaryData | null>(null);

  const fetchReport = async () => {
    if (!fromDate || !toDate) {
      toastMessage('Please select both dates', 'error');
      return;
    }

    setLoading(true);
    try {
      const response = await getOrdersSummary({
        from_date: fromDate,
        to_date: toDate,
      });

      if (response.success) {
        setData(response.data as SummaryData);
      } else {
        toastMessage('Failed to fetch report', 'error');
      }
    } catch (error: any) {
      toastMessage(error.message || 'Error fetching report', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, []);

  const formatCurrency = (value: number) => {
    return `₹${value.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
  };

  const KPICard = ({ icon: Icon, label, value, suffix = '' }: any) => (
    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">{label}</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">
            {typeof value === 'number' ? value.toLocaleString() : value}
            <span className="text-lg ml-1 text-gray-500">{suffix}</span>
          </p>
        </div>
        <div className="bg-brandColor/10 p-3 rounded-lg">
          <Icon className="h-6 w-6 text-brandColor" />
        </div>
      </div>
    </div>
  );

  return (
    <div className="h-full overflow-y-auto">
      {/* Header - matches sidebar header height */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-4 sticky top-0 z-10">
        <div className="flex items-center justify-between gap-4">
          {/* Left Content */}
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0">
              <div className="p-2 bg-brandColor rounded-lg text-white">
                <BarChart3 className="h-5 w-5" />
              </div>
            </div>
            <div>
              <h1 className="text-lg font-semibold text-gray-900 dark:text-white">Orders Summary</h1>
              <p className="text-xs text-gray-500 dark:text-gray-400">KPI overview & top performers</p>
            </div>
          </div>

          {/* Right Actions - Date Range Filter */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
            <button
              onClick={fetchReport}
              disabled={loading}
              className="px-3 py-1.5 bg-brandColor text-white text-sm rounded-lg hover:bg-brandColor/90 disabled:opacity-50 font-medium transition-all"
            >
              {loading ? 'Loading...' : 'Refresh'}
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 space-y-6">
        {/* Explanation */}
        <div className="flex gap-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
          <svg className="h-4 w-4 text-blue-500 dark:text-blue-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
          <div className="text-sm text-blue-800 dark:text-blue-300">
            <p className="font-semibold mb-0.5">About this report</p>
            <p className="text-blue-700 dark:text-blue-400 leading-relaxed">
              High-level snapshot of all Shopify orders in the selected period. KPI cards cover total volume, revenue, and customer counts.
              <strong> Top Tags</strong> highlights which CRM tags drove the most orders.
              <strong> Top Agents</strong> shows agent performance derived from tag suffixes (e.g. <em>Sales-BPO-Abrar → Abrar</em>).
            </p>
          </div>
        </div>

      {/* KPI Cards */}
      {data && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <KPICard icon={Package} label="Total Orders" value={data.summary.total_orders} />
            <KPICard icon={DollarSign} label="Total Revenue" value={formatCurrency(data.summary.total_revenue)} />
            <KPICard icon={TrendingUp} label="Paid Revenue" value={formatCurrency(data.summary.paid_revenue)} />
            <KPICard icon={Users} label="Avg Order Value" value={formatCurrency(data.summary.avg_order_value)} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <KPICard icon={Package} label="Unique Customers" value={data.summary.unique_customers} />
            <KPICard icon={TrendingUp} label="Paid Orders" value={data.summary.paid_orders} />
            <KPICard icon={AlertCircle} label="Refunded Orders" value={data.summary.refunded_orders} />
            <KPICard icon={AlertCircle} label="Cancelled Orders" value={data.summary.cancelled_orders} />
          </div>

          {/* Top Tags & Agents */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Top Tags */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Top Tags</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-700/50">
                    <tr>
                      <th className="px-6 py-3 text-left font-medium text-gray-700 dark:text-gray-300">Tag</th>
                      <th className="px-6 py-3 text-right font-medium text-gray-700 dark:text-gray-300">Orders</th>
                      <th className="px-6 py-3 text-right font-medium text-gray-700 dark:text-gray-300">Revenue</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {data.top_tags.map((tag) => (
                      <tr key={tag.tag} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                        <td className="px-6 py-4 text-gray-900 dark:text-gray-100">{tag.tag}</td>
                        <td className="px-6 py-4 text-right text-gray-900 dark:text-gray-100">{tag.order_count}</td>
                        <td className="px-6 py-4 text-right text-gray-900 dark:text-gray-100">{formatCurrency(tag.revenue)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Top Agents */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Top Agents</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-700/50">
                    <tr>
                      <th className="px-6 py-3 text-left font-medium text-gray-700 dark:text-gray-300">Agent</th>
                      <th className="px-6 py-3 text-right font-medium text-gray-700 dark:text-gray-300">Orders</th>
                      <th className="px-6 py-3 text-right font-medium text-gray-700 dark:text-gray-300">Revenue</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {data.top_agents.map((agent) => (
                      <tr key={agent.agent} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                        <td className="px-6 py-4 text-gray-900 dark:text-gray-100">{agent.agent}</td>
                        <td className="px-6 py-4 text-right text-gray-900 dark:text-gray-100">{agent.order_count}</td>
                        <td className="px-6 py-4 text-right text-gray-900 dark:text-gray-100">{formatCurrency(agent.revenue)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      )}

      {loading && (
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-brandColor"></div>
            <p className="mt-4 text-gray-600 dark:text-gray-400">Loading report...</p>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default OrdersSummaryReport;
